import { USER_ROLES } from '../constants';
import {
  ARIA_ESCALATION_TARGET_TYPE,
  ARIA_NOTIFICATION_TYPE,
  getAriaNotificationMeta,
  resolveAriaNotificationPath,
} from '../constants/ariaNotificationTypes';
import {
  getCommunityNotificationMeta,
  PARTNER_GROUP_CHAT_MESSAGE_TARGET_TYPE,
  PARTNER_GROUP_JOIN_REQUEST_TARGET_TYPE,
  PARTNER_GROUP_MEMBER_AUTO_JOINED_TARGET_TYPE,
  PARTNER_GROUP_MEMBER_JOINED_TARGET_TYPE,
  resolveCommunityNotificationPath,
} from '../constants/communityNotificationTypes';
import {
  getEventNotificationMeta,
  resolveEventNotificationPath,
} from '../constants/eventNotificationTypes';
import {
  getVettingNotificationMeta,
  pickVettingApplicationId,
  resolveVettingNotificationPath,
  VETTING_APPLICATION_TARGET_TYPE,
  VETTING_NOTIFICATION_TYPE,
} from '../constants/vettingNotificationTypes';
import {
  getPartnerNotificationMeta,
  PARTNER_PROFILE_TARGET_TYPE,
  resolvePartnerNotificationPath,
} from '../constants/partnerNotificationTypes';

export const PARTNER_EVENT_TARGET_TYPE = 'partner_event';
export { ARIA_ESCALATION_TARGET_TYPE };
export { VETTING_APPLICATION_TARGET_TYPE };
export { PARTNER_PROFILE_TARGET_TYPE };
export {
  PARTNER_GROUP_CHAT_MESSAGE_TARGET_TYPE,
  PARTNER_GROUP_JOIN_REQUEST_TARGET_TYPE,
  PARTNER_GROUP_MEMBER_AUTO_JOINED_TARGET_TYPE,
  PARTNER_GROUP_MEMBER_JOINED_TARGET_TYPE,
};

/** FCM webpush links that must not override event deep links. */
const GENERIC_PORTAL_PATHS = new Set([
  '/',
  '/dashboard',
  '/partner-dashboard',
  '/login',
  '/partner-login',
  '/notifications',
  '/partner-notifications',
]);

/**
 * Flatten inbox row, FCM data payload, or API record into routing fields.
 */
export const normalizeNotificationRouteInput = (source = {}) => {
  const nested = source.data ?? {};
  return {
    type: source.type ?? nested.type ?? source.notificationType ?? nested.notificationType ?? null,
    targetType: source.targetType ?? nested.targetType ?? null,
    targetId: source.targetId ?? nested.targetId ?? null,
    eventId: source.eventId ?? nested.eventId ?? nested.event_id ?? null,
    url: source.url ?? nested.url ?? null,
    path: source.path ?? nested.path ?? null,
    role: source.role ?? nested.role ?? source.userRole ?? nested.userRole ?? null,
    screen: source.screen ?? nested.screen ?? null,
  };
};

/** Event id from data.eventId or partner_event targetId. */
export const pickNotificationEventId = (source = {}) => {
  const { eventId, targetId, targetType } = normalizeNotificationRouteInput(source);
  if (eventId && String(eventId).trim()) {
    return String(eventId).trim();
  }
  if (targetId && String(targetId).trim()) {
    if (!targetType || targetType === PARTNER_EVENT_TARGET_TYPE) {
      return String(targetId).trim();
    }
  }
  return null;
};

export const isPartnerEventTargetNotification = (source = {}) => {
  const { targetType, type } = normalizeNotificationRouteInput(source);
  if (targetType === PARTNER_EVENT_TARGET_TYPE) {
    return true;
  }
  return Boolean(getEventNotificationMeta(type));
};

export const isCommunityPortalNotification = (source = {}) => {
  const { targetType, type } = normalizeNotificationRouteInput(source);
  if (
    targetType === PARTNER_GROUP_JOIN_REQUEST_TARGET_TYPE ||
    targetType === PARTNER_GROUP_CHAT_MESSAGE_TARGET_TYPE ||
    targetType === PARTNER_GROUP_MEMBER_AUTO_JOINED_TARGET_TYPE ||
    targetType === PARTNER_GROUP_MEMBER_JOINED_TARGET_TYPE
  ) {
    return true;
  }
  return Boolean(getCommunityNotificationMeta(type));
};

export const isVettingPortalNotification = (source = {}) => {
  const { targetType, type } = normalizeNotificationRouteInput(source);
  if (targetType === VETTING_APPLICATION_TARGET_TYPE) {
    return true;
  }
  return Boolean(getVettingNotificationMeta(type));
};

export const isAriaPortalNotification = (source = {}) => {
  const { targetType, type } = normalizeNotificationRouteInput(source);
  if (
    targetType === ARIA_ESCALATION_TARGET_TYPE ||
    targetType === ARIA_NOTIFICATION_TYPE.ARIA_HUMAN_HANDOFF_REQUESTED
  ) {
    return true;
  }
  return Boolean(getAriaNotificationMeta(type));
};

export const isPartnerPortalNotification = (source = {}) => {
  const { targetType, type } = normalizeNotificationRouteInput(source);
  if (targetType === PARTNER_PROFILE_TARGET_TYPE) {
    return true;
  }
  return Boolean(getPartnerNotificationMeta(type));
};

export const isSupportedPortalNotification = (source = {}) =>
  isPartnerEventTargetNotification(source) ||
  isCommunityPortalNotification(source) ||
  isVettingPortalNotification(source) ||
  isPartnerPortalNotification(source) ||
  isAriaPortalNotification(source);

const normalizePath = (raw) => {
  if (!raw || typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return new URL(trimmed).pathname;
    }
  } catch {
    return null;
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const isGenericPortalPath = (path) => {
  if (!path) {
    return true;
  }
  const pathname = path.split('?')[0].replace(/\/+$/, '') || '/';
  return GENERIC_PORTAL_PATHS.has(pathname);
};

const resolveViewerRole = (fields, userRole, meta) => {
  const roleHint = fields.role;
  return (
    userRole ||
    meta?.recipientRole ||
    (roleHint === USER_ROLES.PARTNER || roleHint === 'partner'
      ? USER_ROLES.PARTNER
      : USER_ROLES.ADMIN)
  );
};

/** Prefer explicit type; fall back to known community/event targetType values. */
export const resolvePortalNotificationType = (source = {}) => {
  const fields = normalizeNotificationRouteInput(source);
  const explicit = String(fields.type || '')
    .trim()
    .toLowerCase();
  if (explicit) {
    return explicit;
  }

  const targetType = String(fields.targetType || '')
    .trim()
    .toLowerCase();
  if (
    getCommunityNotificationMeta(targetType) ||
    getEventNotificationMeta(targetType) ||
    getVettingNotificationMeta(targetType) ||
    getPartnerNotificationMeta(targetType) ||
    getAriaNotificationMeta(targetType)
  ) {
    return targetType;
  }

  if (targetType === VETTING_APPLICATION_TARGET_TYPE) {
    return VETTING_NOTIFICATION_TYPE.VETTING_DECISION_FINALIZED;
  }

  if (targetType === ARIA_ESCALATION_TARGET_TYPE) {
    return ARIA_NOTIFICATION_TYPE.ARIA_HUMAN_HANDOFF_REQUESTED;
  }

  return null;
};

/**
 * Single resolver for inbox rows, foreground toast, and browser notification clicks.
 * Event notification types take priority over FCM url/link (often site root or dashboard).
 */
export const resolvePortalNotificationPath = (source = {}, userRole = null) => {
  const fields = normalizeNotificationRouteInput(source);
  const eventId = pickNotificationEventId(source);
  const applicationId = pickVettingApplicationId(source);
  const resolvedType = resolvePortalNotificationType(source);
  const eventMeta = getEventNotificationMeta(resolvedType);
  const communityMeta = getCommunityNotificationMeta(resolvedType);
  const vettingMeta = getVettingNotificationMeta(resolvedType);
  const partnerMeta = getPartnerNotificationMeta(resolvedType);
  const ariaMeta = getAriaNotificationMeta(resolvedType);
  const viewerRole = resolveViewerRole(
    fields,
    userRole,
    eventMeta ?? communityMeta ?? vettingMeta ?? partnerMeta ?? ariaMeta,
  );

  if (partnerMeta || fields.targetType === PARTNER_PROFILE_TARGET_TYPE) {
    return resolvePartnerNotificationPath({
      type: resolvedType || PARTNER_PROFILE_TARGET_TYPE,
      viewerRole,
    });
  }

  if (vettingMeta || fields.targetType === VETTING_APPLICATION_TARGET_TYPE) {
    return resolveVettingNotificationPath({
      type: resolvedType || VETTING_APPLICATION_TARGET_TYPE,
      applicationId: applicationId || fields.targetId,
      viewerRole,
    });
  }

  if (
    ariaMeta ||
    fields.targetType === ARIA_ESCALATION_TARGET_TYPE ||
    fields.targetType === ARIA_NOTIFICATION_TYPE.ARIA_HUMAN_HANDOFF_REQUESTED
  ) {
    return resolveAriaNotificationPath({ viewerRole });
  }

  if (communityMeta) {
    return resolveCommunityNotificationPath({ type: resolvedType, viewerRole });
  }

  if (eventMeta) {
    return resolveEventNotificationPath({ type: resolvedType, eventId, viewerRole });
  }

  const fromUrl = normalizePath(fields.url);
  if (fromUrl && !isGenericPortalPath(fromUrl)) {
    return fromUrl;
  }

  const fromPath = normalizePath(fields.path);
  if (fromPath && !isGenericPortalPath(fromPath)) {
    return fromPath;
  }

  if (!eventId) {
    return viewerRole === USER_ROLES.PARTNER ? '/partner-notifications' : '/notifications';
  }

  if (fields.targetType === PARTNER_EVENT_TARGET_TYPE) {
    return viewerRole === USER_ROLES.PARTNER
      ? `/partner-events/${eventId}`
      : `/events/${eventId}?mode=review`;
  }

  const isPartner = viewerRole === USER_ROLES.PARTNER;
  const isModerationFeedbackRoute =
    fields.screen === 'moderation-feedback' || resolvedType === 'event_moderation_feedback';

  if (isModerationFeedbackRoute) {
    return isPartner ? `/partner-events/${eventId}/moderation-feedback` : `/events/${eventId}`;
  }

  return isPartner ? `/partner-events/${eventId}` : `/events/${eventId}`;
};

/** Normalize path + search for comparison (e.g. `/events/1?mode=review`). */
export const normalizePortalPath = (path) => {
  const trimmed = String(path ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.split('#')[0];
};

export const getCurrentPortalPath = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return normalizePortalPath(`${window.location.pathname}${window.location.search}`);
};

export const isSamePortalPath = (targetPath) => {
  const target = normalizePortalPath(targetPath);
  if (!target) {
    return false;
  }
  return getCurrentPortalPath() === target;
};

/**
 * Navigate including query string (e.g. ?mode=review).
 * If the target is already open, reload so data is fresh (e.g. partner resubmitted while admin is on review).
 */
export const navigatePortalPath = (navigate, path) => {
  if (!path) {
    return;
  }

  const target = normalizePortalPath(path);

  if (isSamePortalPath(target)) {
    window.location.reload();
    return;
  }

  if (!navigate) {
    return;
  }

  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) {
    navigate(path);
    return;
  }

  navigate({
    pathname: path.slice(0, queryIndex),
    search: path.slice(queryIndex),
  });
};
